/**
 * API-Funktionen für das Rating Widget
 * Alle Kommunikationen mit dem Server
 */
import { generateSecurityToken } from './security.js'
import { saveRatingToStorage, getRatingFromStorage, isCurrentPageRated } from './storage.js'
import { RATING_ENDPOINT_TYPE } from './constants.js'

/**
 * Sendet eine Bewertung an den Server
 * @param {string} pid - Die PID der Seite
 * @param {number} rating - Die Bewertung (1-5)
 * @returns {Promise<Object>} - Die Server-Antwort
 */
export async function submitRating(pid, rating) {
    if (!pid || typeof rating !== 'number' || isNaN(rating)) {
        throw new Error('Ungültige Bewertungsdaten')
    }

    try {
        // Sicherheitstoken generieren
        const securityToken = generateSecurityToken()

        // FormData für den POST-Request erstellen
        const formData = new FormData()
        formData.append('pid', pid)
        formData.append('rating', rating)

        // CSRF-Token hinzufügen
        const csrfToken = window.csrfToken

        if (csrfToken) {
            formData.append('tx_rating_csrf_token', csrfToken)
        }

        // URL erstellen
        const url = `/index.php?type=${RATING_ENDPOINT_TYPE}`

        // Header mit Token
        const headers = {
            'X-Security-Token': securityToken,
        }

        // Request senden
        const response = await fetch(url, {
            method: 'POST',
            headers: headers,
            body: formData
        })

        if (!response.ok) {
            throw new Error(`Server antwortete mit ${response.status}`)
        }

        // Antwort parsen
        const result = await response.json()

        // Prüfen, ob "bereits bewertet" Meldung
        if (!result.success && result.message && result.message.includes('bereits bewertet')) {
            // Existierende Bewertung aus dem Server-Response extrahieren
            let existingRating = rating
            if (result.debug && result.debug.existing_rating && result.debug.existing_rating.rating) {
                existingRating = result.debug.existing_rating.rating
            }

            return {
                success: false,
                alreadyRated: true,
                message: result.message,
                existingRating: existingRating,
                averageRating: result.data?.average_rating,
            }
        }

        // Fehlerfall behandeln
        if (!result.success) {
            throw new Error(result.message || 'Fehler beim Senden der Bewertung')
        }

        // Bewertung in localStorage speichern
        saveRatingToStorage(pid, rating)

        // Erfolgsfall
        return {
            success: true,
            averageRating: result.data?.average_rating,
            message: result.message,
        }
    } catch (error) {
        throw error
    }
}

/**
 * Prüft, ob der Benutzer bereits bewertet hat
 * @param {string} pid - Die PID der Seite
 * @returns {Promise<boolean>} - True wenn bereits bewertet
 */
export function checkIfRated(pid) {
    return new Promise((resolve) => {
        try {
            // Statt nur zu prüfen, ob ein Rating für diese PID existiert,
            // prüfen wir, ob die aktuelle URL (ohne Domain) bereits bewertet wurde
            const currentPageRated = isCurrentPageRated(pid);
            resolve(currentPageRated);
            
            // Falls wir trotzdem auch das gespeicherte Rating abrufen möchten
            // (z.B. um die Bewertung anzuzeigen):
            const storedRating = getRatingFromStorage(pid);
        } catch (error) {
            // Reduziertes Logging bei nicht kritischem Fehler
            resolve(false);
        }
    });
}

/**
 * Sendet Feedback-Text an den Server
 * @param {Object} data - Feedback-Daten (enthält ratingPid, ratingValue und feedback)
 * @returns {Promise<Object>} - Die Server-Antwort
 */
export async function submitFeedbackText(data) {
    if (!data.ratingPid || !data.feedback) {
        throw new Error('Ungültige Feedback-Daten')
    }

    try {
        // Sicherheitstoken generieren
        const securityToken = generateSecurityToken()

        // FormData für den POST-Request erstellen
        const formData = new FormData()
        formData.append('pid', data.ratingPid)
        formData.append('rating', data.ratingValue || 0)
        formData.append('feedback', data.feedback)

        // CSRF-Token hinzufügen
        const csrfToken = window.csrfToken

        if (csrfToken) {
            formData.append('tx_rating_csrf_token', csrfToken)
        }

        // URL erstellen
        const url = `/index.php?type=1743405237` // FEEDBACK_TEXT_ENDPOINT_TYPE

        // Header mit Token
        const headers = {
            'X-Security-Token': securityToken,
        }

        // Request senden
        const response = await fetch(url, {
            method: 'POST',
            headers: headers,
            body: formData
        })

        if (!response.ok) {
            throw new Error(`Server antwortete mit ${response.status}`)
        }

        // Antwort parsen
        const result = await response.json()

        // Fehlerfall behandeln
        if (!result.success) {
            throw new Error(result.message || 'Fehler beim Senden des Feedbacks')
        }

        // Erfolgsfall - vollständige Serverantwort zurückgeben
        return {
            success: true,
            message: result.message || 'Vielen Dank für Ihr Feedback!',
            data: result.data || {}
        }
    } catch (error) {
        throw error
    }
}

/**
 * Sendet eine E-Mail-Adresse an den Server
 * @param {Object} data - E-Mail-Daten (enthält ratingPid, ratingValue und email)
 * @returns {Promise<Object>} - Die Server-Antwort
 */
export async function submitEmail(data) {
    if (!data.ratingPid || !data.email) {
        throw new Error('Ungültige E-Mail-Daten')
    }

    try {
        // Sicherheitstoken generieren
        const securityToken = generateSecurityToken()

        // FormData für den POST-Request erstellen
        const formData = new FormData()
        formData.append('pid', data.ratingPid)
        formData.append('rating', data.ratingValue || 0)
        formData.append('email', data.email)

        // CSRF-Token hinzufügen
        const csrfToken = window.csrfToken

        if (csrfToken) {
            formData.append('tx_rating_csrf_token', csrfToken)
        }

        // URL erstellen
        const url = `/index.php?type=1743405238` // EMAIL_ENDPOINT_TYPE

        // Header mit Token
        const headers = {
            'X-Security-Token': securityToken,
        }

        // Request senden
        const response = await fetch(url, {
            method: 'POST',
            headers: headers,
            body: formData
        })

        if (!response.ok) {
            throw new Error(`Server antwortete mit ${response.status}`)
        }

        // Antwort parsen
        const result = await response.json()

        // Fehlerfall behandeln
        if (!result.success) {
            throw new Error(result.message || 'Fehler beim Senden der E-Mail-Adresse')
        }

        // Erfolgsfall - vollständige Serverantwort zurückgeben
        return {
            success: true,
            message: result.message || 'Vielen Dank für Ihre E-Mail-Adresse!',
            data: result.data || {}
        }
    } catch (error) {
        throw error
    }
}

/**
 * Legacy-Funktion: Sendet ein Feedback an den Server
 * Diese Funktion dient der Abwärtskompatibilität und entscheidet basierend auf den
 * übergebenen Daten, ob submitFeedbackText oder submitEmail aufgerufen wird
 * @param {Object} data - Feedback-Daten
 * @returns {Promise<Object>} - Serverdaten
 */
export async function submitFeedback(data) {
    // Je nach vorhandenen Daten die passende Funktion aufrufen
    if (data.email) {
        return submitEmail(data)
    } else if (data.feedback) {
        return submitFeedbackText(data)
    } else {
        // Fallback für leere Daten
        return {
            success: false,
            message: 'Keine gültigen Daten zum Senden vorhanden',
        }
    }
}
